import {cdnImage} from '@/util/cdnImage';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {MUIContainer} from '@/components/MUI-Components';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontWeight} from '@/styles/tokens';
const logoGrandview = cdnImage('white-label-exchange-development-new/logo-grandviewresearch.png');
const logoCoinlaw = cdnImage('white-label-exchange-development-new/logo-coinlaw.png');
// ─── Static data ─────────────────────────────────────────────────────────────
const statsData = [
    {
        value: '$264.3B',
        description: 'where the crypto exchange market is headed by 2030.',
        sourceLogo: logoGrandview,
        sourceAlt: 'Grandview Research logo',
        sourceLabel: 'grandviewresearch',
        sourceLink: 'https://www.grandviewresearch.com/industry-analysis/cryptocurrency-exchange-platform-market-report'
    },
    {
        value: '$85.75B',
        description: 'estimated global crypto exchange revenue by 2026.',
        sourceLogo: logoCoinlaw,
        sourceAlt: 'Coinlaw logo',
        sourceLabel: 'coinlaw',
        sourceLink:
            'https://coinlaw.io/crypto-exchange-statistics/#:~:text=Recent%20Developments,deep%20and%20persistent%20liquidity.%E2%80%8B'
    },
    {
        value: '35%',
        description: "North America's share of global exchange revenue today.",
        sourceLogo: logoCoinlaw,
        sourceAlt: 'Coinlaw logo',
        sourceLabel: 'coinlaw',
        sourceLink:
            'https://coinlaw.io/crypto-exchange-statistics/#:~:text=Recent%20Developments,deep%20and%20persistent%20liquidity.%E2%80%8B'
    }
];

// ─── Module-level sx constants ────────────────────────────────────────────────
const sectionSx = {
    bgcolor: colors.background.default
} as const;

const headingSx = {
    fontWeight: fontWeight.extrabold,
    fontSize: {xs: 32, md: 52},
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: 1.3,
    mb: {xs: 4, md: 6}
} as const;

const cardSx = {
    border: '1px solid rgba(0,0,0,0.25)',
    borderRadius: borderRadius['3xl'],
    px: '25px',
    py: '14px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    background: 'radial-gradient(circle at 50% 50%, rgba(37,99,235,0.05) 0%, rgba(37,99,235,0) 70%), #ffffff'
} as const;

const statValueSx = {
    fontWeight: fontWeight.extrabold,
    fontSize: {xs: 36, md: 48},
    color: colors.primary.main,
    letterSpacing: '-0.02em',
    lineHeight: '72px',
    textAlign: 'center'
} as const;

const statDescSx = {
    fontSize: 16,
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: '24px'
} as const;

const sourceRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
} as const;

const sourceLabelSx = {
    fontSize: 16,
    fontStyle: 'italic',
    color: '#8a8a8a',
    lineHeight: '24px'
} as const;

const bodyTextSx = {
    fontSize: 16,
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: '31px',
    maxWidth: 1000,
    mx: 'auto',
    mt: {xs: 4, md: 6}
} as const;
// ─────────────────────────────────────────────────────────────────────────────

const WhiteLabelMarketWindow = () => {
    return (
        <Box sx={sectionSx} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
            <MUIContainer>
                <Typography variant="h2" className="font-headers" sx={headingSx}>
                    The Crypto Exchange Market Is Booming — Here&apos;s Your Window
                </Typography>

                <Grid container spacing={3}>
                    {statsData.map((stat, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Box sx={cardSx}>
                                <Typography className="font-headers" sx={statValueSx}>
                                    {stat.value}
                                </Typography>
                                <Typography className="font-regular-text" sx={statDescSx}>
                                    {stat.description}
                                </Typography>
                                <Box sx={sourceRowSx}>
                                    <DisplayImage src={stat.sourceLogo} alt={stat.sourceAlt} width={20} height={20} />
                                    <Typography component="span" className="font-regular-text" sx={sourceLabelSx}>
                                        <a
                                            href={stat.sourceLink}
                                            target="_blank"
                                            rel="nofollow"
                                            className="font-headers"
                                            style={{color: '#000', textDecoration: 'underline'}}
                                        >
                                            {(() => {
                                                try {
                                                    return new URL(stat.sourceLink).hostname.replace('www.', '');
                                                } catch {
                                                    return stat.sourceLink;
                                                }
                                            })()}
                                        </a>
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Typography className="font-regular-text" sx={bodyTextSx}>
                    This rapid growth creates a prime opportunity for businesses to launch their own trading platforms and capture new
                    revenue streams. The operators capturing the most market share today aren&apos;t the ones who built the best technology
                    — they&apos;re the ones who launched fastest. A white label crypto exchange platform enables you to{' '}
                    <Box component="strong" sx={{fontWeight: fontWeight.bold}}>
                        enter the market at the right time
                    </Box>
                    , with infrastructure already aligned to trading, compliance, and scalability requirements.
                </Typography>
            </MUIContainer>
        </Box>
    );
};

export default WhiteLabelMarketWindow;
